import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Shield, Truck, RefreshCw, XCircle, FileText, Lock, Package, MapPin, Clock, AlertTriangle, Building2 } from 'lucide-react';

const PolicySection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-neutral-200">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 px-4 flex items-center justify-between hover:bg-neutral-50 transition-colors"
        data-testid={`policy-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-[#FF4D00]" />
          <span className="font-semibold text-neutral-800 uppercase tracking-wide text-sm">{title}</span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-neutral-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-neutral-500" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-6 pt-2 text-sm text-neutral-600 leading-relaxed">
          {children}
        </div>
      )}
    </div>
  );
};

const Policies = () => {
  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#FF4D00] to-[#E04400] text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold mb-2" data-testid="policies-title">Terms of Service</h1>
          <p className="text-white/80">RSD Accessories Pvt. Ltd. - Policies & Guidelines</p>
        </div>
      </div>
      
      {/* Policy Sections */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          
          {/* Overview */}
          <PolicySection title="Overview" icon={FileText} defaultOpen={true}>
            <p className="mb-4">
              Welcome to RSD Accessories Pvt. Ltd. ("RSD", "we", "us", or "our"). These Terms of Service govern your use of our website <strong>rsdaccessories.com</strong> and the purchase of products from our platform.
            </p>
            <p className="mb-4">
              By accessing or using our website, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this site.
            </p>
            <p className="mb-4">
              <strong>Company Details:</strong><br />
              RSD Accessories Pvt. Ltd.<br />
              A Brand of New Eon<br />
              Sector-57, Noida, Uttar Pradesh, India<br />
              Contact: +91 8595459161<br />
              Email: contact@rsdaccessories.com
            </p>
            <p>
              These terms were last updated on January 2026. We reserve the right to modify these terms at any time without prior notice.
            </p>
          </PolicySection>
          
          {/* Warranty Policy */}
          <PolicySection title="Warranty Policy" icon={Shield}>
            <p className="mb-4">
              <strong>Warranty Coverage:</strong> All RSD products come with a <strong>6 Months Manufacturer Warranty</strong> from the date of purchase against manufacturing defects.
            </p>
            <p className="mb-4">
              <strong>What is Covered:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Manufacturing defects in materials and workmanship</li>
              <li>Functional failures under normal use conditions</li>
              <li>Electronic component failures (not due to physical damage)</li>
              <li>Charging/connectivity issues due to internal faults</li>
            </ul>
            <p className="mb-4">
              <strong>What is NOT Covered:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Physical damage (broken, bent, crushed, or burnt products)</li>
              <li>Water/liquid damage</li>
              <li>Damage due to misuse, negligence, or accidents</li>
              <li>Unauthorized modifications or repairs</li>
              <li>Normal wear and tear</li>
              <li>Damage caused by use with incompatible devices</li>
              <li>Products without valid proof of purchase</li>
            </ul>
            <p className="mb-4">
              <strong>How to Claim Warranty:</strong>
            </p>
            <ol className="list-decimal pl-6 mb-4 space-y-1">
              <li>Contact us at contact@rsdaccessories.com with your Order ID</li>
              <li>Provide photos/videos showing the defect</li>
              <li>Keep your original invoice/order confirmation</li>
              <li>Ship the product to our service center (shipping paid by customer)</li>
              <li>Warranty decision will be communicated within 7-10 business days</li>
            </ol>
            <p className="text-neutral-500 text-xs">
              Note: RSD reserves the right to repair, replace, or refund at its sole discretion based on product availability and nature of defect.
            </p>
          </PolicySection>
          
          {/* Replacement Policy */}
          <PolicySection title="Replacement Policy" icon={RefreshCw}>
            <p className="mb-4">
              <strong>7-Day Replacement Policy:</strong> We offer hassle-free replacement within <strong>7 days</strong> of delivery for products received in damaged or defective condition.
            </p>
            <p className="mb-4">
              <strong>Eligibility for Replacement:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Product received is damaged during shipping</li>
              <li>Product received is different from what was ordered</li>
              <li>Product is Dead on Arrival (DOA) - not working out of the box</li>
              <li>Missing accessories or components mentioned in the product listing</li>
            </ul>
            <p className="mb-4">
              <strong>Replacement Process:</strong>
            </p>
            <ol className="list-decimal pl-6 mb-4 space-y-1">
              <li>Report the issue within 7 days of delivery via email or WhatsApp</li>
              <li>Provide unboxing video (mandatory for damage claims)</li>
              <li>Our team will verify and approve the replacement request</li>
              <li>Return pickup will be arranged at no extra cost</li>
              <li>Replacement will be shipped within 3-5 business days after receiving the return</li>
            </ol>
            <p className="mb-4">
              <strong>Conditions:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Product must be in original packaging with all accessories</li>
              <li>All tags, labels, and warranty cards must be intact</li>
              <li>Product should not be used (except for testing)</li>
              <li>Original invoice must be included with the return</li>
            </ul>
            <p className="text-neutral-500 text-xs">
              Note: Replacement is subject to stock availability. If the same product is unavailable, we will offer a similar product or full refund.
            </p>
          </PolicySection>
          
          {/* Cancellation Policy */}
          <PolicySection title="Cancellation Policy" icon={XCircle}>
            <p className="mb-4">
              <strong>Order Cancellation:</strong> You may cancel your order under the following conditions:
            </p>
            <p className="mb-4">
              <strong>Before Shipment:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Orders can be cancelled anytime before dispatch at no charge</li>
              <li>Full refund will be processed within 5-7 business days</li>
              <li>Contact us immediately via WhatsApp or email to cancel</li>
            </ul>
            <p className="mb-4">
              <strong>After Shipment:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Orders cannot be cancelled once shipped</li>
              <li>You may refuse delivery, and the product will be returned to us</li>
              <li>Refund will be processed after we receive the returned product</li>
              <li>Shipping charges may be deducted from the refund amount</li>
            </ul>
            <p className="mb-4">
              <strong>How to Cancel:</strong>
            </p>
            <ol className="list-decimal pl-6 mb-4 space-y-1">
              <li>WhatsApp: +91 8595459161</li>
              <li>Email: contact@rsdaccessories.com</li>
              <li>Provide your Order ID and reason for cancellation</li>
            </ol>
            <p className="text-neutral-500 text-xs">
              Note: For prepaid orders, refund will be credited to the original payment method. For COD orders, no action needed if cancelled before shipment.
            </p>
          </PolicySection>
          
          {/* Return & Refund Policy */}
          <PolicySection title="Return & Refund Policy" icon={RefreshCw}>
            <p className="mb-4">
              <strong>Return Policy:</strong> We accept returns within <strong>7 days</strong> of delivery under specific conditions.
            </p>
            <p className="mb-4">
              <strong>Returnable Items:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Defective or damaged products</li>
              <li>Wrong product delivered</li>
              <li>Product significantly different from description</li>
            </ul>
            <p className="mb-4">
              <strong>Non-Returnable Items:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Products damaged due to customer misuse</li>
              <li>Products without original packaging/accessories</li>
              <li>Products purchased during special sales (final sale items)</li>
              <li>Earbuds/Neckbands (hygiene reasons) - unless defective</li>
            </ul>
            <p className="mb-4">
              <strong>Refund Process:</strong>
            </p>
            <ol className="list-decimal pl-6 mb-4 space-y-1">
              <li>Return request approved → Product picked up</li>
              <li>Quality check at our warehouse (2-3 business days)</li>
              <li>Refund initiated after successful QC</li>
              <li>Amount credited within 5-7 business days</li>
            </ol>
            <p className="mb-4">
              <strong>Refund Methods:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Original payment method (Credit/Debit Card, UPI, Net Banking)</li>
              <li>Bank transfer (for COD orders - bank details required)</li>
            </ul>
            <p className="text-neutral-500 text-xs">
              Note: Shipping charges are non-refundable unless the return is due to our error.
            </p>
          </PolicySection>
          
          {/* Copyright Policy */}
          <PolicySection title="Copyright Policy" icon={FileText}>
            <p className="mb-4">
              <strong>Intellectual Property Rights:</strong> All content on this website including but not limited to text, graphics, logos, images, product designs, and software is the property of RSD Accessories Pvt. Ltd. and is protected by Indian and international copyright laws.
            </p>
            <p className="mb-4">
              <strong>Prohibited Actions:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Reproducing, distributing, or displaying any content without written permission</li>
              <li>Using our trademarks, logos, or brand names without authorization</li>
              <li>Copying product images or descriptions for commercial use</li>
              <li>Scraping or harvesting data from our website</li>
              <li>Creating derivative works based on our content</li>
            </ul>
            <p className="mb-4">
              <strong>Permitted Use:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Viewing and browsing for personal, non-commercial purposes</li>
              <li>Sharing product links on social media</li>
              <li>Printing product information for personal reference</li>
            </ul>
            <p className="mb-4">
              <strong>Trademark Notice:</strong> "RSD", "RSD Accessories", "New Eon", and associated logos are registered trademarks of RSD Accessories Pvt. Ltd.
            </p>
            <p className="text-neutral-500 text-xs">
              Violations of our copyright policy may result in legal action under the Indian Copyright Act, 1957.
            </p>
          </PolicySection>
          
          {/* Privacy Policy */}
          <PolicySection title="Privacy Policy" icon={Lock}>
            <p className="mb-4">
              <strong>Data Collection:</strong> We collect information to provide better services to our customers. This includes:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Personal Information: Name, email, phone number, shipping address</li>
              <li>Payment Information: Processed securely through Razorpay (we don't store card details)</li>
              <li>Device Information: Browser type, IP address, device identifiers</li>
              <li>Usage Data: Pages visited, products viewed, time spent on site</li>
            </ul>
            <p className="mb-4">
              <strong>How We Use Your Data:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Processing and fulfilling orders</li>
              <li>Communicating order updates and delivery status</li>
              <li>Providing customer support</li>
              <li>Sending promotional offers (with your consent)</li>
              <li>Improving our website and services</li>
            </ul>
            <p className="mb-4">
              <strong>Data Protection:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>SSL encryption for all data transmission</li>
              <li>Secure payment processing via Razorpay</li>
              <li>Limited access to personal data (authorized personnel only)</li>
              <li>Regular security audits and updates</li>
            </ul>
            <p className="mb-4">
              <strong>Third-Party Sharing:</strong> We do not sell your personal information. Data may be shared with:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Shipping partners (for delivery purposes)</li>
              <li>Payment processors (for transaction processing)</li>
              <li>Legal authorities (if required by law)</li>
            </ul>
            <p className="mb-4">
              <strong>Your Rights:</strong> You can request access, correction, or deletion of your personal data by contacting contact@rsdaccessories.com.
            </p>
          </PolicySection>
          
          {/* Out of Warranty Products */}
          <PolicySection title="Out of Warranty Products" icon={Package}>
            <p className="mb-4">
              <strong>Paid Repair Services:</strong> For products outside the warranty period or with damage not covered under warranty, we offer paid repair services.
            </p>
            <p className="mb-4">
              <strong>Process:</strong>
            </p>
            <ol className="list-decimal pl-6 mb-4 space-y-1">
              <li>Contact our service center with product details and issue description</li>
              <li>Send the product for inspection (shipping paid by customer)</li>
              <li>Receive repair quote within 3-5 business days</li>
              <li>Confirm and pay for repair</li>
              <li>Product repaired and shipped back (shipping charges apply)</li>
            </ol>
            <p className="mb-4">
              <strong>Repair Charges:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Inspection fee: ₹99 (waived if repair is done)</li>
              <li>Repair charges: Varies based on product and issue</li>
              <li>Part replacement: At actual cost + service charge</li>
            </ul>
            <p className="mb-4">
              <strong>Non-Repairable Products:</strong> Some products may be beyond economical repair. In such cases, we offer:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Exchange discount on new purchase</li>
              <li>Product return (inspection fee applies)</li>
            </ul>
            <p className="text-neutral-500 text-xs">
              Contact: contact@rsdaccessories.com for out-of-warranty support.
            </p>
          </PolicySection>
          
          {/* Shipping Information */}
          <PolicySection title="Shipping Information" icon={Truck}>
            <p className="mb-4">
              <strong>Delivery Partners:</strong> We partner with leading courier services including Delhivery, BlueDart, DTDC, and India Post for reliable delivery across India.
            </p>
            <p className="mb-4">
              <strong>Shipping Charges:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li><strong>FREE Shipping:</strong> On orders above ₹499</li>
              <li><strong>Standard Shipping:</strong> ₹100 for orders below ₹499</li>
              <li><strong>Express Shipping:</strong> Available at additional cost (select cities)</li>
            </ul>
            <p className="mb-4">
              <strong>Delivery Timeline:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Metro Cities (Delhi NCR, Mumbai, Bangalore, etc.): 2-4 business days</li>
              <li>Tier 2 Cities: 4-6 business days</li>
              <li>Remote Areas: 7-10 business days</li>
            </ul>
            <p className="mb-4">
              <strong>Serviceable Pincodes:</strong> We deliver to most pincodes across India. Enter your pincode during checkout to verify serviceability.
            </p>
            <p className="mb-4">
              <strong>Order Processing:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Orders placed before 2 PM are processed same day</li>
              <li>Orders placed after 2 PM are processed next business day</li>
              <li>No processing on Sundays and national holidays</li>
            </ul>
          </PolicySection>
          
          {/* Tracking Your Order */}
          <PolicySection title="Tracking Your Order" icon={MapPin}>
            <p className="mb-4">
              <strong>Order Tracking:</strong> Once your order is shipped, you will receive tracking details via:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Email notification with tracking link</li>
              <li>SMS with tracking number and courier details</li>
              <li>WhatsApp update (if opted in)</li>
            </ul>
            <p className="mb-4">
              <strong>How to Track:</strong>
            </p>
            <ol className="list-decimal pl-6 mb-4 space-y-1">
              <li>Click on the tracking link in your email/SMS</li>
              <li>Or visit the courier partner's website and enter tracking number</li>
              <li>Or contact us with your Order ID for status update</li>
            </ol>
            <p className="mb-4">
              <strong>Tracking Status Meanings:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li><strong>Order Placed:</strong> Order received and confirmed</li>
              <li><strong>Processing:</strong> Order being packed at warehouse</li>
              <li><strong>Shipped:</strong> Handed over to courier partner</li>
              <li><strong>In Transit:</strong> On the way to your city</li>
              <li><strong>Out for Delivery:</strong> With delivery agent, arriving today</li>
              <li><strong>Delivered:</strong> Successfully delivered</li>
            </ul>
            <p className="text-neutral-500 text-xs">
              Note: Tracking updates may take 12-24 hours to reflect after shipment.
            </p>
          </PolicySection>
          
          {/* Delayed Delivery */}
          <PolicySection title="Delayed Delivery" icon={Clock}>
            <p className="mb-4">
              <strong>Delivery Delays:</strong> While we strive for timely delivery, delays may occur due to:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>High order volume during sales/festivals</li>
              <li>Courier partner operational issues</li>
              <li>Weather conditions or natural disasters</li>
              <li>Address verification issues</li>
              <li>Remote location accessibility</li>
              <li>Government restrictions or curfews</li>
            </ul>
            <p className="mb-4">
              <strong>If Your Delivery is Delayed:</strong>
            </p>
            <ol className="list-decimal pl-6 mb-4 space-y-1">
              <li>Check tracking status for latest updates</li>
              <li>Wait for 2-3 additional business days beyond expected date</li>
              <li>Contact our support team if delay exceeds 5 days</li>
            </ol>
            <p className="mb-4">
              <strong>Compensation for Delays:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Delays beyond 7 days (metros) / 10 days (other areas): Eligible for shipping fee refund</li>
              <li>Delays beyond 15 days: Option to cancel and get full refund</li>
            </ul>
            <p className="mb-4">
              <strong>Failed Delivery Attempts:</strong> If delivery fails:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Courier will attempt delivery 2-3 times</li>
              <li>Update your contact number if unreachable</li>
              <li>Product returned to us after 3 failed attempts</li>
              <li>Re-shipping charges may apply</li>
            </ul>
          </PolicySection>
          
          {/* B2B GST Policy */}
          <PolicySection title="B2B GST Policy" icon={Building2}>
            <p className="mb-4">
              <strong>GST Invoice for Business Purchases:</strong> RSD Accessories provides GST-compliant invoices for B2B (Business to Business) orders.
            </p>
            <p className="mb-4">
              <strong>GST Details:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>GST Rate: 18% (on most electronic accessories)</li>
              <li>HSN Codes: Applicable as per product category</li>
              <li>GST Invoice: Automatically generated for all orders</li>
            </ul>
            <p className="mb-4">
              <strong>For Bulk/B2B Orders:</strong>
            </p>
            <ol className="list-decimal pl-6 mb-4 space-y-1">
              <li>Contact us at contact@rsdaccessories.com</li>
              <li>Provide your GSTIN for verification</li>
              <li>Receive special B2B pricing quotation</li>
              <li>Place order with GST-compliant invoice</li>
            </ol>
            <p className="mb-4">
              <strong>Input Tax Credit (ITC):</strong> Business customers can claim Input Tax Credit on purchases. Ensure:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>GSTIN is provided at the time of order</li>
              <li>Billing address matches GST registration</li>
              <li>Invoice is downloaded and filed properly</li>
            </ul>
            <p className="mb-4">
              <strong>Our GST Details:</strong><br />
              Company: RSD Accessories Pvt. Ltd.<br />
              GSTIN: [Available on invoice]<br />
              State: Uttar Pradesh (09)
            </p>
            <p className="text-neutral-500 text-xs">
              For distributor partnerships and bulk pricing, visit our "Become a Distributor" page or contact contact@rsdaccessories.com.
            </p>
          </PolicySection>
          
          {/* Contact for Policy Queries */}
          <PolicySection title="Contact for Policy Queries" icon={AlertTriangle}>
            <p className="mb-4">
              <strong>Need Help with Policies?</strong> Our customer support team is here to assist you.
            </p>
            <p className="mb-4">
              <strong>Contact Channels:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li><strong>All Queries:</strong> contact@rsdaccessories.com</li>
              <li><strong>WhatsApp:</strong> +91 8595459161</li>
              <li><strong>Phone:</strong> +91 8595459161</li>
            </ul>
            <p className="mb-4">
              <strong>Support Hours:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Monday to Saturday: 10:00 AM - 7:00 PM IST</li>
              <li>Sunday: Closed</li>
              <li>Response Time: Within 24-48 hours</li>
            </ul>
            <p className="mb-4">
              <strong>Grievance Officer:</strong><br />
              For escalations and grievances:<br />
              Name: Customer Grievance Cell<br />
              Email: contact@rsdaccessories.com<br />
              Phone: +91 8595459161<br />
              Response: Within 48 hours
            </p>
          </PolicySection>
          
        </div>
        
        {/* Footer Note */}
        <div className="mt-8 text-center text-sm text-neutral-500">
          <p>© 2026 RSD Accessories Pvt. Ltd. All Rights Reserved.</p>
          <p className="mt-1">These policies are effective from January 2026 and may be updated periodically.</p>
        </div>
      </div>
    </div>
  );
};

export default Policies;
